import { Link } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Lightning, Key, Path, Stack, ListChecks, Flask, ArrowSquareOut, CheckCircle,
} from "@phosphor-icons/react";

const FIELDS = [
  { icon: Path, title: "Platform key", plain: "A short internal id, lowercase, no spaces.", eg: "pipedrive, zendesk, my_crm", why: "Used to save the connector and to auto-find its logo." },
  { icon: Path, title: "Display name", plain: "The friendly name people will see in the dropdown.", eg: "Pipedrive, Acme CRM", why: "Purely cosmetic." },
  { icon: Stack, title: "Category", plain: "A loose grouping.", eg: "crm, support, marketing, database", why: "Helps organise a growing catalog." },
  { icon: Path, title: "Base URL", plain: "The web address the app's API lives at. Copy it from the app's API docs.", eg: "https://api.pipedrive.com/v1", why: "Use {curly braces} for account-specific parts, e.g. https://{subdomain}.zendesk.com/api/v2." },
  { icon: Key, title: "Authentication type", plain: "How the app checks it's really you. Pick the one the app's API docs mention.", eg: "API Key, Bearer token, Basic (user + password), OAuth2", why: "API Key / Bearer are the most common. If unsure, search the app's docs for 'authentication'." },
  { icon: Key, title: "Credential fields", plain: "The boxes the user will fill when connecting (e.g. an API token). You just name them here.", eg: "api_token, api_key, access_token", why: "Mark secrets so they're hidden and encrypted." },
  { icon: ListChecks, title: "Health check", plain: "One simple, safe endpoint we call to confirm the connection works.", eg: "/users/me, /account", why: "This powers the green 'Connected' status." },
  { icon: Stack, title: "Objects", plain: "The kinds of records you want to move — each becomes something you can migrate.", eg: "contacts, deals, tickets", why: "For each, give the list path (e.g. /contacts) and, if you want to write, a create path." },
  { icon: Path, title: "Pagination", plain: "How the app hands back long lists across multiple pages.", eg: "offset, page, cursor, link", why: "If unsure, start with 'none' — you can refine later." },
  { icon: ListChecks, title: "Field discovery", plain: "How we learn the columns/fields of each object.", eg: "sample (look at a few records) is the easy default", why: "'describe' uses a metadata endpoint; 'declared' lets you list fields by hand." },
  { icon: Flask, title: "Trial / sandbox", plain: "Turn on if the app offers a no-login demo so people can try it instantly.", eg: "Off for most business apps", why: "When on, users get a 'Use trial account' button — no credentials needed." },
];

const TRIALS = [
  { name: "Salesforce", type: "CRM", need: "Free Developer Edition (permanent)", url: "https://developer.salesforce.com/signup", ready: true },
  { name: "HubSpot", type: "CRM / Marketing", need: "Free developer test account", url: "https://developers.hubspot.com/", ready: true },
  { name: "Pipedrive", type: "CRM", need: "Free 14-day trial → API token", url: "https://www.pipedrive.com/", ready: true },
  { name: "Zendesk", type: "Support", need: "Free trial → subdomain + API token", url: "https://www.zendesk.com/register/", ready: true },
  { name: "Zoho CRM", type: "CRM", need: "Free edition", url: "https://www.zoho.com/crm/", ready: false },
  { name: "Freshdesk", type: "Support", need: "Free trial", url: "https://freshdesk.com/signup", ready: false },
  { name: "Airtable", type: "Database / Sheets", need: "Free plan → personal access token", url: "https://airtable.com/", ready: false },
  { name: "Mailchimp", type: "Marketing", need: "Free plan → API key", url: "https://mailchimp.com/", ready: false },
  { name: "Stripe", type: "Payments", need: "Test mode (free, instant)", url: "https://dashboard.stripe.com/register", ready: false },
  { name: "Google Sheets", type: "Spreadsheet", need: "Any Google account + API key / service account", url: "https://sheets.google.com/", ready: true },
];

export default function ConnectorGuide() {
  return (
    <Layout>
      <PageHeader title="How to set up a connector"
        subtitle="A plain-language guide — no technical background needed"
        actions={<Link to="/connector-studio"><Button variant="outline" className="rounded-xl gap-2" data-testid="back-to-studio"><ArrowLeft size={16} /> Back to Studio</Button></Link>} />

      <div className="p-8 max-w-4xl space-y-10">
        {/* What is a connector */}
        <section className="bg-[#C2410C]/5 border border-[#C2410C]/20 rounded-xl p-5">
          <div className="flex items-center gap-2 font-head font-semibold text-stone-900 mb-2">
            <Lightning size={18} weight="fill" className="text-[#C2410C]" /> What is a connector?
          </div>
          <p className="text-sm text-stone-600 leading-relaxed">
            A connector is a one-time description of an app (like Pipedrive or Zendesk) so this platform knows how to
            talk to it. You set it up <strong>once</strong> here; afterwards anyone in your team just picks it from a
            list and plugs in their login — exactly like Salesforce or HubSpot already work. No coding, no waiting.
          </p>
        </section>

        {/* Three ways */}
        <section>
          <h2 className="font-head font-semibold text-lg text-stone-900 mb-4">Three ways to add one</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { n: "1", t: "Guided form (easiest)", d: "Fill in the boxes below step-by-step. Best if you don't want to touch code." },
              { n: "2", t: "Import from OpenAPI", d: "If the app publishes an OpenAPI/Swagger link, paste it and we draft the connector for you." },
              { n: "3", t: "JSON (advanced)", d: "Paste a full manifest. For technical users who know the shape already." },
            ].map((c) => (
              <div key={c.n} className="bg-white border border-stone-200 rounded-2xl shadow-card p-4">
                <div className="w-7 h-7 rounded-full bg-[#C2410C] text-white flex items-center justify-center text-sm font-bold">{c.n}</div>
                <div className="font-medium text-stone-900 mt-3 text-sm">{c.t}</div>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Field by field */}
        <section>
          <h2 className="font-head font-semibold text-lg text-stone-900 mb-1">What each box means</h2>
          <p className="text-sm text-stone-500 mb-5">Tip: almost everything you need is in the app's own "API documentation" page — search it for "authentication" and "endpoints".</p>
          <div className="space-y-3">
            {FIELDS.map((f) => (
              <div key={f.title} className="bg-white border border-stone-200 rounded-2xl shadow-card p-4 flex gap-4">
                <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center shrink-0">
                  <f.icon size={18} className="text-[#C2410C]" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-stone-900">{f.title}</div>
                  <p className="text-sm text-stone-600 mt-0.5">{f.plain}</p>
                  <div className="text-xs text-stone-500 mt-1"><span className="uppercase tracking-wider text-stone-400">Example:</span> <span className="font-mono">{f.eg}</span></div>
                  <div className="text-xs text-stone-500 mt-0.5">{f.why}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* A worked example */}
        <section className="bg-white border border-stone-200 rounded-2xl shadow-card p-5">
          <h2 className="font-head font-semibold text-lg text-stone-900 mb-3">A 60-second example: Pipedrive</h2>
          <ol className="space-y-2 text-sm text-stone-600 list-decimal pl-5">
            <li>Platform key <span className="font-mono">pipedrive</span>, Display name <span className="font-mono">Pipedrive</span>.</li>
            <li>Base URL <span className="font-mono">https://api.pipedrive.com/v1</span> (from Pipedrive's API docs).</li>
            <li>Auth type <span className="font-mono">API key (in query)</span>; add a credential field <span className="font-mono">api_token</span> (mark it secret).</li>
            <li>Health check path <span className="font-mono">/users/me</span>.</li>
            <li>Add an object <span className="font-mono">persons</span> → list path <span className="font-mono">/persons</span>, records path <span className="font-mono">data</span>.</li>
            <li>Save. Done — Pipedrive now appears on the Connections page for everyone.</li>
          </ol>
        </section>

        {/* Trial accounts */}
        <section>
          <h2 className="font-head font-semibold text-lg text-stone-900 mb-1">Apps with free trial / developer accounts</h2>
          <p className="text-sm text-stone-500 mb-4">Great for testing a connector end-to-end without touching production data. Sign up (free), grab the API key/token, and connect.</p>
          <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                  <th className="px-5 py-3 font-semibold">App</th>
                  <th className="px-5 py-3 font-semibold">Type</th>
                  <th className="px-5 py-3 font-semibold">What you need</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Sign up</th>
                </tr>
              </thead>
              <tbody>
                {TRIALS.map((t) => (
                  <tr key={t.name} className="border-b border-stone-100 hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-5 py-3 font-medium text-stone-900">{t.name}</td>
                    <td className="px-5 py-3 text-xs text-stone-500">{t.type}</td>
                    <td className="px-5 py-3 text-xs text-stone-600">{t.need}</td>
                    <td className="px-5 py-3">
                      {t.ready
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-xl text-[10px] gap-1"><CheckCircle size={11} weight="fill" /> Built-in</Badge>
                        : <Badge variant="outline" className="rounded-xl text-[10px] text-stone-500">Add via Studio</Badge>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <a href={t.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#C2410C] text-xs font-medium hover:underline">
                        Open <ArrowSquareOut size={12} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-stone-400 mt-3">"Built-in" = the connector already ships with the platform; just add your credentials. "Add via Studio" = create it with the guided form (takes a couple of minutes).</p>
        </section>
      </div>
    </Layout>
  );
}
